/**
 * @file The Recompute now contract: one `analyticsRecompute/latest` document holding the last
 * request to start the `analytics-drain` job. Written only by the `recomputeAnalytics` callable in
 * `functions/main` through the admin SDK, never by a client; read by any analytics reader. The claim
 * transaction replaces the whole document, so an old error never outlives a new request, and one
 * request per `ANALYTICS_RECOMPUTE_COOLDOWN_SECONDS` is allowed for the whole tenant. A failed start
 * still uses the cooldown. `requestedAt` and `retryAt` are `Date#toISOString()` strings.
 */

export const ANALYTICS_RECOMPUTE_PATH = 'analyticsRecompute/latest';

/** Recompute now: one request per 15 minutes for the whole tenant. */
export const ANALYTICS_RECOMPUTE_COOLDOWN_SECONDS = 900;

export const ANALYTICS_RECOMPUTE_STATUSES = ['requested', 'started', 'failed', 'notStarted'] as const;

export type AnalyticsRecomputeStatus = (typeof ANALYTICS_RECOMPUTE_STATUSES)[number];

/** The last Recompute now request; written only by `recomputeAnalytics`'s admin SDK. */
export type AnalyticsRecompute = {
  requestedBy: string;
  /** `Date#toISOString()`, taken inside the claim transaction. */
  requestedAt: string;
  status: AnalyticsRecomputeStatus;
  /** The Cloud Run operation name of a started drain execution, else null. */
  operation: string | null;
  /** Why the start failed, else null. */
  error: string | null;
};

export type RecomputeAnalyticsResponse = {
  status: Extract<AnalyticsRecomputeStatus, 'started' | 'notStarted'>;
  requestedAt: string;
  retryAt: string;
};

/** The details of a `resource-exhausted` refusal and of a failed start after the claim. */
export type AnalyticsRecomputeCooldownDetails = { retryAt: string };
